import React from "react";

export const LEARNING_CONDITIONS = (
  <>
    <p>Factors to consider while choosing a Learning Condition to improve:</p>
    <ul>
      <li>
        <b>Opportunities for growth</b> - which conditions have lower scores?
      </li>
      <li>
        <b>Work already underway</b> - what would enhance work you are already
        doing?{" "}
      </li>
      <li>
        <b>Areas of interest</b> - what motivates you?
      </li>
      <li>
        <b>Capacity</b> - what are you able to take on?{" "}
      </li>
      <li>
        <b>Readiness</b> - where is a good place to start?
      </li>
      <li>
        <b>School or district initiatives</b> - where do you see alignment?
      </li>
      <li>
        <b>Other data</b> - is there overlap with other relevant data you
        collect?
      </li>
    </ul>
    <p>
      All the conditions are important for students’ learning and well-being, so
      any choice you make can benefit students!
    </p>
    <p>
      Learn about Cultivate scores: <a href="about/">About Cultivate</a>
    </p>
  </>
);

export const GROUP_DISCUSSION = (
  <>
    <p>While looking for patterns and trends in the table:</p>
    <ul>
      <li>
        Select “Highest” or “Lowest” to highlight relative areas of strength or
        opportunities for growth, respectively
        <ul>
          <li>
            This is useful information, but other factors – including capacity,
            interest, and readiness – can play an important role in determining
            where to focus improvement efforts
          </li>
        </ul>
      </li>
      <li>
        Select “Most increased” or “Most decreased” on spring reports to
        highlight the Learning Conditions that changed the most since the fall
        administration
        <ul>
          <li>
            Exploring which factors or specific practices might have contributed
            to those shifts can help educators adjust and plan for improvement!
          </li>
        </ul>
      </li>
    </ul>
    <p>Remember:</p>
    <ul>
      <li>
        Cultivate scores are best understood in <b>context</b>, so it is
        important to dig into individual reports and to take into account other
        data and information to help sense-make
      </li>
    </ul>
  </>
);

export const EXPLORE_RESPONSES = (
  <>
    <p>While exploring:</p>
    <ul>
      <li>Celebrate areas of strength</li>
      <li>Look for areas for improvement</li>
      <li>Reflect on why students might have answered the way they did</li>
      <li>Note any questions or curiosities you have</li>
    </ul>
    <p>Remember:</p>
    <ul>
      <li>
        Students are reliable and valid reporters of their own experiences –
        their feedback about school predicts their educational trajectories
      </li>
      <li>
        Try to remain open to what your students have to say and avoid excuses,
        especially where your perception might differ
      </li>
      <li>
        Discrepancies are often due to issues of <b>intent</b> vs. <b>impact</b>{" "}
        – even when done with the best of intentions, educators’ efforts don’t
        always have the desired impact on students
      </li>
      <li>
        Learning students aren’t having positive experiences can be
        disappointing – but it also presents an opportunity to find out why and
        partner with them to plan ways to better meet their needs
      </li>
    </ul>
  </>
);

export const CONSIDER_EXPERIENCES = (
  <>
    <p>Consider:</p>
    <ul>
      <li>Areas of strength</li>
      <li>Opportunities for improvement</li>
      <li>Factors that might account for differences</li>
      <li>Any questions or curiosities that arise</li>
    </ul>
    <p>Remember:</p>
    <ul>
      <li>
        Average scores for different student groups are useful, but even{" "}
        <b>within groups students have a range of experiences</b> – keep this in
        mind as you look across groups and avoid making assumptions
      </li>
      <li>
        Points of contradiction or confusion are opportunities for deeper
        inquiry – ask yourself what you can learn from your students about
        variations in their experiences
      </li>
    </ul>
  </>
);

export const TAKE_ACTION = (
  <>
    <p>Be sure to:</p>
    <ul>
      <li>Discuss areas of curiosity</li>
      <li>Clarify areas of confusion</li>
      <li>Ask any questions you still have</li>
      <li>
        Brainstorm all the ways you might address student feedback and make
        improvements
      </li>
    </ul>
    <p>Remember:</p>
    <ul>
      <li>
        Give yourself time and grace as you process, examine, and analyze
        results
      </li>
      <li>
        Improving student experiences isn’t something you need to do on your
        own!
        <ul>
          <li>
            Working with colleagues helps you effect change, while also
            promoting more consistent experiences for students{" "}
          </li>
          <li>
            Students are uniquely valuable partners in the improvement process –
            bring them into the conversation and allow them to help you as you
            try to improve their experiences{" "}
          </li>
        </ul>
      </li>
      <li>
        Consider how elevating the voices of students’ aligns with your own
        goals; ground yourself in your “why” before taking action
      </li>
    </ul>
  </>
);

export const LEARNING_BELIEFS = (
  <>
    <p>
      While looking through students’ overall responses about their learning
      beliefs, consider how the different learning conditions might influence
      their feelings about each question. For example:
    </p>
    <ul>
      <li>
        <b>What are all the teacher actions</b> that might foster a strong sense
        of belonging and lead students to believe “I am valued?”
      </li>
      <li>
        <b>What kinds of opportunities can a teacher create</b> to build
        students’ sense of agency, ensuring they say, “I am empowered?”
      </li>
      <li>
        <b>What are the structures a teacher could implement</b> to support
        students’ use of monitoring strategies, leading them to say “I am
        capable?”
      </li>
      <li>
        <b>What are the messages a teacher could send</b> about students’
        ability to learn, allowing them to believe “I can grow?”
      </li>
      <li>
        <b>How might a teacher’s choices about content contribute</b> to a
        feeling of relevance for students, leading them to “find class
        meaningful?”
      </li>
    </ul>
  </>
);

export const IntroWording = ({ lcEssential }) => (
  <>
    The {lcEssential.name} in a classroom have profound implications for
    students’ development – they shape students’ daily experiences, learning
    beliefs, academic success, and well-being.
  </>
);
